import { buildUrl, fetchWithRetry } from './utils';

/**
 * Login and Registration API
 * Handles authentication, user registration, and session management
 */

export const logregApi = {
  /**
   * Login with email and password
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async login(email, password) {
    try {
      const response = await fetchWithRetry(buildUrl('logreg', '/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password
        })
      });

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Login failed'
        };
      }

      // Return ALL data from backend response
      return {
        success: true,
        data: data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  /**
   * Register a new business account
   * @param {object} formData - Business registration data
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async registerBusiness(formData) {
    try {
      const response = await fetch(buildUrl('logreg', '/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Registration failed'
        };
      }

      return {
        success: true,
        data: data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  /**
   * Refresh session token using refresh key
   * @param {string} refreshKey
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async refreshSessionToken(refreshKey) {
    try {
      const response = await fetch(buildUrl('logreg', '/refreshsessionkey'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshKey
        })
      });

      const data = await response.json();

      if (!data.success) {
        return {
          success: false,
          error: data.error || 'Failed to refresh session'
        };
      }

      return {
        success: true,
        data: {
          sessionToken: data.sessionToken,  // Backend returns 'sessionToken', not 'token'
          refreshKey: data.refreshKey
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  /**
   * Logout and invalidate refresh key
   * @param {string} refreshKey
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async logout(refreshKey) {
    try {
      const response = await fetch(buildUrl('logreg', '/logout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshKey
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        error: data.success ? null : (data.error || 'Logout failed')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  /**
   * Validate current session token
   * @param {string} sessionToken
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async validateSession(sessionToken) {
    try {
      const response = await fetch(buildUrl('logreg', '/validatesession'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionToken
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Session validation failed')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  /**
   * Request password reset email
   * @param {string} email
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async requestPasswordReset(email) {
    try {
      const response = await fetch(buildUrl('logreg', '/passwordresetemail'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to send password reset email')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await fetch(buildUrl('logreg', '/resetpassword'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resetToken: token,
          newPassword
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to reset password')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  /**
   * Register employee with invite token
   * @param {string} inviteToken - Invitation token from email
   * @param {object} registrationData - Employee registration data
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async registerEmployee(inviteToken, registrationData) {
    try {
      const response = await fetch(buildUrl('logreg', '/registeremployee'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inviteToken,
          ...registrationData
        })
      });

      const data = await response.json();

      return {
        success: data.success,
        data: data.success ? data : null,
        error: data.success ? null : (data.error || 'Failed to register employee')
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  },

  /**
   * Send employee invites (business/manager only)
   * @param {string} sessionToken - Session token
   * @param {array} invites - Array of invite objects {email, isManager, jobTitle, startDate}
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  async sendEmployeeInvites(sessionToken, invites) {
    try {
      const response = await fetch(buildUrl('logreg', '/sendemployeeinvites'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionToken,
          invites
        })
      });

      const data = await response.json();

      return {
        success: data.success !== false,
        data: data,
        error: data.success === false ? (data.error || 'Failed to send invites') : null
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Network error occurred'
      };
    }
  }
};
